import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI, tenantAPI } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Building2,
  Users,
  CheckCircle,
  XCircle,
  TrendingUp,
  ArrowRight,
  Plus,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        dashboardAPI.getStats(),
        tenantAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Tenants",
      value: stats?.total_tenants || 0,
      icon: Building2,
      color: "bg-indigo-100 text-indigo-600",
      trend: "+12%",
    },
    {
      title: "Active Tenants",
      value: stats?.active_tenants || 0,
      icon: CheckCircle,
      color: "bg-emerald-100 text-emerald-600",
      trend: "+8%",
    },
    {
      title: "Total Users",
      value: stats?.total_users || 0,
      icon: Users,
      color: "bg-violet-100 text-violet-600",
      trend: "+24%",
    },
    {
      title: "Active Users",
      value: stats?.active_users || 0,
      icon: TrendingUp,
      color: "bg-amber-100 text-amber-600",
      trend: "+18%",
    },
  ];

  const pieData = [
    { name: "Active", value: stats?.active_tenants || 0 },
    { name: "Suspended", value: (stats?.total_tenants || 0) - (stats?.active_tenants || 0) },
  ];

  const COLORS = ["#10B981", "#EF4444"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="superadmin-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Super Admin Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Manage tenants and monitor platform health
          </p>
        </div>
        <Link to="/tenants">
          <Button className="btn-primary" data-testid="create-tenant-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Tenant
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card card-hover" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {stat.trend} this month
                  </p>
                </div>
                <div className={`stat-card-icon ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant Status Chart */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Tenant Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="text-sm text-slate-600">
                    {item.name}: {item.value}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Tenants */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Recent Tenants</CardTitle>
            <Link to="/tenants">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tenants.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No tenants yet</p>
              ) : (
                tenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{tenant.name}</p>
                        <p className="text-sm text-slate-500">{tenant.domain}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        variant={tenant.status === "active" ? "default" : "destructive"}
                        className="capitalize"
                      >
                        {tenant.status}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {tenant.subscription_plan}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
